import {
  getMealPhotoFn,
  getWorkoutMealsOptions,
  saveWorkoutMealFn,
  uploadMealPhotoFn,
} from "@/actions/workout-meal/handler";
import type { MealData } from "@/api/db/schema/workout-meals";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";

export function MealPhoto({ photoKey }: { photoKey: string }) {
  const { data, isError, refetch } = useQuery({
    queryKey: ["mealPhoto", photoKey],
    queryFn: () => getMealPhotoFn({ data: { key: photoKey } }),
    staleTime: 240000,
    refetchInterval: 240000,
  });
  if (isError)
    return (
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={() => void refetch()}
      >
        Fotoğrafı tekrar yükle
      </Button>
    );
  return data ? (
    <img
      src={data.url}
      alt="Öğün fotoğrafı"
      className="max-h-64 w-full rounded-lg object-contain"
    />
  ) : (
    <p className="text-muted-foreground text-sm">Fotoğraf yükleniyor...</p>
  );
}
const macros = [
  { key: "calories", label: "Kalori (kcal)" },
  { key: "protein", label: "Protein (g)" },
  { key: "carbs", label: "Karbonhidrat (g)" },
  { key: "fats", label: "Yağ (g)" },
] as const;
const emptyMeal: MealData = {
  calories: 0,
  protein: 0,
  carbs: 0,
  fats: 0,
  photoKey: null,
};
function MealForm({
  programId,
  date,
  phase,
  initialMeal,
  isCoachingClient,
}: {
  programId: string;
  date: string;
  phase: "pre" | "post";
  initialMeal: MealData | null;
  isCoachingClient: boolean;
}) {
  const queryClient = useQueryClient();
  const [meal, setMeal] = useState(initialMeal ?? emptyMeal);
  const [file, setFile] = useState<File | null>(null);
  const mutation = useMutation({
    mutationFn: async () => {
      let photoKey = meal.photoKey;
      if (file) {
        const upload = await uploadMealPhotoFn({
          data: {
            contentType: file.type as "image/jpeg" | "image/png" | "image/webp",
            size: file.size,
          },
        });
        const response = await fetch(upload.url, {
          method: "PUT",
          headers: { "Content-Type": file.type },
          body: file,
        });
        if (!response.ok) throw new Error("Fotoğraf yüklenemedi.");
        photoKey = upload.key;
      }
      const result = await saveWorkoutMealFn({
        data: { programId, date, phase, meal: { ...meal, photoKey } },
      });
      return { ...result, photoKey };
    },
    onSuccess: (result) => {
      setMeal((previous) => ({ ...previous, photoKey: result.photoKey }));
      setFile(null);
      toast.success(
        result.syncedToCoaching
          ? "Öğün kaydedildi ve danışan takibine aktarıldı."
          : "Öğün kaydedildi.",
      );
      for (const queryKey of [
        ["workoutMeals", date],
        ["getMetricByDate", date],
        ["getTodayMetric"],
        ["getMyMetrics"],
        ["admin", "user-activity"],
      ])
        void queryClient.invalidateQueries({ queryKey });
    },
    onError: () =>
      toast.error(
        "Öğün kaydedilemedi. Bilgilerinizi koruduk; tekrar deneyebilirsiniz.",
      ),
  });
  return (
    <form
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault();
        mutation.mutate();
      }}
    >
      <fieldset disabled={mutation.isPending} className="space-y-4">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {macros.map(({ key, label }) => (
            <div key={key} className="space-y-2">
              <Label htmlFor={`${phase}-${key}`}>{label}</Label>
              <Input
                id={`${phase}-${key}`}
                type="number"
                min={0}
                max={key === "calories" ? 10000 : 1000}
                step="0.1"
                required
                value={meal[key]}
                onChange={(event) =>
                  setMeal((previous) => ({
                    ...previous,
                    [key]: Number(event.target.value),
                  }))
                }
              />
            </div>
          ))}
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${phase}-photo`}>
            Öğün fotoğrafı (isteğe bağlı)
          </Label>
          <Input
            id={`${phase}-photo`}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={(event) => {
              const selected = event.target.files?.[0];
              if (!selected) return;
              if (
                !["image/jpeg", "image/png", "image/webp"].includes(
                  selected.type,
                ) ||
                selected.size > 10 * 1024 * 1024
              ) {
                toast.error("En fazla 10 MB JPEG, PNG veya WebP seçin.");
                event.target.value = "";
                return;
              }
              setFile(selected);
            }}
          />
          {file && (
            <p className="text-muted-foreground text-sm">
              Seçilen fotoğraf: {file.name}
            </p>
          )}
        </div>
        {meal.photoKey && (
          <>
            <MealPhoto photoKey={meal.photoKey} />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setMeal((previous) => ({ ...previous, photoKey: null }));
                setFile(null);
              }}
            >
              Fotoğrafı kayıttan kaldır
            </Button>
          </>
        )}
        <Button type="submit">
          {mutation.isPending ? "Kaydediliyor..." : "Öğünü Kaydet"}
        </Button>
      </fieldset>
      {isCoachingClient && (
        <p className="text-muted-foreground text-xs">
          Makrolar ve fotoğraf {date} tarihli danışan takibinizde koçunuza
          görünür. Düzenlemeler toplamı günceller.
        </p>
      )}
    </form>
  );
}
export function WorkoutMealCard({
  programId,
  date,
  phase,
  isCoachingClient,
}: {
  programId: string;
  date: string;
  phase: "pre" | "post";
  isCoachingClient: boolean;
}) {
  const { data, isPending, isError, refetch } = useQuery(
    getWorkoutMealsOptions(date),
  );
  const existing = data?.find((entry) => entry.phase === phase)?.data ?? null;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">
          {phase === "pre" ? "Antrenman Öncesi Öğün" : "Antrenman Sonrası Öğün"}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isPending ? (
          <p>Öğün bilgileri yükleniyor...</p>
        ) : isError ? (
          <Button
            type="button"
            variant="outline"
            onClick={() => void refetch()}
          >
            Öğün bilgilerini tekrar yükle
          </Button>
        ) : (
          <MealForm
            key={`${programId}-${date}-${phase}`}
            programId={programId}
            date={date}
            phase={phase}
            initialMeal={existing}
            isCoachingClient={isCoachingClient}
          />
        )}
      </CardContent>
    </Card>
  );
}
